/**
 * ServiceOrdersPage — `/ordens-servico`: as ordens de serviço da empresa.
 *
 * Módulo GRÁTIS, sem gate de plano nem de papel.
 *
 * ─── O DESENHO OFICIAL NUMA LISTA DE DOCUMENTOS ─────────────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes`, `/clients`, `/catalog` e
 * `/trabalhos`. O que precisou ser TRADUZIDO aqui (o exercício do
 * #182/#188/#195/#197):
 *
 * · O EYEBROW diz o "onde" — a família de que a lista faz parte.
 * · O VALOR AO VIVO é a soma do recorte visível, e a regra de quem entra nela é
 *   de NEGÓCIO: OS `cancelled` é serviço que não vai ser cobrado e fica FORA,
 *   com a contagem do que ficou de fora escrita na sublinha ("no silent caps").
 *   Mora no módulo puro `serviceOrdersListFilters`.
 * · O BOTÃO DE VOLTAR fica, como em `/trabalhos`: `/ordens-servico` NÃO é
 *   destino da navegação inferior (que tem Início, Orçamentos, Novo, Clientes e
 *   Catálogo), então sem ele não há saída barata da tela.
 * · O STATUS deixou de ser `<Tabs>` e virou faixa de botões planos: alternador
 *   de aba é troca de CONTEXTO (#25) e status é RECORTE. Como esta tela não tem
 *   troca de contexto nenhuma, não sobrou `<Tabs>` — e os cinco rótulos entram
 *   na faixa compacta pela mesma régua medida no #191 (a contagem vai no
 *   `title`/`aria-label`, onde não custa pixel).
 * · NÃO ganhou modo de seleção nem exportação: as duas existem em `/quotes`
 *   porque lá existem ações em massa e uma planilha para exportar. Este módulo
 *   não tem nenhuma das duas, e o desenho transfere a ESTRUTURA, não features
 *   que a tela não possui.
 *
 * ─── UM DEFEITO PRÉ-EXISTENTE CORRIGIDO DE PASSAGEM ─────────────────────────
 *
 * O card imprimia a data agendada com `new Date(scheduled_date)`. A coluna é
 * DATE (date-only, meia-noite UTC), então em UTC-3 isso imprime o DIA ANTERIOR
 * — a família #114/#54. Hoje passa por `localDateFromIsoDatePart`, que é o
 * helper que a Agenda já usava para o mesmo dado.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  ArrowUpDown,
  Calendar,
  Check,
  ClipboardList,
  Plus,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import {
  useServiceOrders,
  SERVICE_ORDER_STATUSES,
  type ServiceOrderStatus,
} from '@/hooks/useServiceOrders';
import {
  DEFAULT_SERVICE_ORDER_SORT,
  SERVICE_ORDER_PERIOD_PRESETS,
  SERVICE_ORDER_SORT_LABELS,
  SERVICE_ORDER_SORT_ORDER,
  SERVICE_ORDER_STATUS_FILTER_OPTIONS,
  countActiveServiceOrderFilters,
  countServiceOrdersByStatus,
  isDeadServiceOrderStatusFilter,
  matchesServiceOrderPeriod,
  matchesServiceOrderStatus,
  matchesServiceOrderValue,
  serviceOrderListTotalLabel,
  serviceOrderMatchesSearch,
  sortServiceOrdersBy,
  summarizeServiceOrderList,
  type ServiceOrderSortOption,
  type ServiceOrderStatusFilter,
} from '@/lib/serviceOrdersListFilters';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { EMPTY_LIST_RANGE, type ListValueRange } from '@/lib/listFilters';
import { PERIOD_PRESET_LABELS, resolvePeriodPreset, type ReportPeriodPreset } from '@/lib/reportPeriod';
import { localDateFromIsoDatePart } from '@/lib/calendarEvents';
import { CONTENT_CARD } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { plural } from '@/lib/plural';
import { cn } from '@/lib/utils';

const STATUS_STYLES: Record<ServiceOrderStatus, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  cancelled: 'bg-muted text-muted-foreground',
};

function statusLabel(status: ServiceOrderStatus): string {
  return SERVICE_ORDER_STATUSES.find((s) => s.value === status)?.label ?? status;
}

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

/**
 * Data por extenso a partir de um carimbo — pelo calendário LOCAL.
 *
 * `created_at` é TIMESTAMP: `date-fns` sobre `new Date` dá o dia local, enquanto
 * cortar a string no 'T' daria o dia em UTC e imprimiria o DIA SEGUINTE para
 * tudo criado depois das 21h (#114).
 */
function timestampDate(value: unknown): string | null {
  if (!value) return null;
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'dd/MM/yyyy', { locale: ptBR });
}

/**
 * Data AGENDADA — coluna DATE (date-only). `new Date(iso)` a jogaria para o dia
 * anterior em UTC-3, que era o defeito que esta tela tinha (ver o cabeçalho).
 */
function scheduledDate(value: unknown): string | null {
  if (!value) return null;
  const date = localDateFromIsoDatePart(String(value));
  if (Number.isNaN(date.getTime())) return null;
  return format(date, 'dd/MM/yyyy', { locale: ptBR });
}

const PERIOD_GROUP_KEY = 'period';

export default function ServiceOrdersPage() {
  const navigate = useNavigate();
  // Só LEITURA: o atalho `?new=true` navega e não reescreve a URL (ver abaixo).
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const queryClient = useQueryClient();

  /**
   * A consulta traz TODAS as ordens; o recorte por status virou trabalho da
   * página porque as contagens dos botões precisam do recorte SEM o status —
   * pedir a lista já filtrada ao hook deixaria todos os outros botões em zero.
   */
  const { orders, isLoading } = useServiceOrders();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ServiceOrderStatusFilter>('all');
  const [sortOption, setSortOption] = useState<ServiceOrderSortOption>(DEFAULT_SERVICE_ORDER_SORT);
  const [periodPreset, setPeriodPreset] = useState<ReportPeriodPreset | null>(null);
  const [valueRange, setValueRange] = useState<ListValueRange>(EMPTY_LIST_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * Atalho "Ordem de serviço" da navegação inferior (`/ordens-servico?new=true`).
   *
   * 🔴 Aqui NÃO se limpa o parâmetro depois de navegar, e isso é a correção de
   * um defeito: `setSearchParams` navega para a rota CORRENTE sem o parâmetro,
   * então chamá-lo logo depois do `navigate` desfazia a navegação — o atalho
   * deixava o usuário parado em `/ordens-servico`, sem formulário nenhum e sem
   * erro. Como o `navigate` é `replace`, a entrada com `?new=true` some do
   * histórico de qualquer jeito. Limpar a URL só faz sentido quando a ação abre
   * um MODAL na própria rota (é o caso de `/recibos` e `/agenda`). Ver #200.
   */
  useEffect(() => {
    if (user && searchParams.get('new') === 'true') {
      navigate('/ordens-servico/nova', { replace: true });
    }
  }, [user, searchParams, navigate]);

  // Refetch when an offline write (or another tab) touches SO data.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders', activeCompany?.id] });
    };
    window.addEventListener('service-order-updated', handler);
    return () => window.removeEventListener('service-order-updated', handler);
  }, [queryClient, activeCompany?.id]);

  /**
   * A faixa do período é resolvida UMA vez por render, e não dentro do
   * `filter`: `resolvePeriodPreset` cria `Date`s a partir de "agora", e chamá-la
   * por ordem compararia cada linha contra um "agora" diferente.
   */
  const periodRange = useMemo(
    () => (periodPreset ? resolvePeriodPreset(periodPreset) : null),
    [periodPreset],
  );

  /** O recorte SEM o status — é ele que alimenta as contagens dos botões. */
  const ordersBeforeStatus = useMemo(
    () =>
      (orders ?? []).filter(
        (order: any) =>
          serviceOrderMatchesSearch(order, search) &&
          matchesServiceOrderPeriod(order, periodRange) &&
          matchesServiceOrderValue(order, valueRange),
      ),
    [orders, search, periodRange, valueRange],
  );

  const statusCounts = useMemo(
    () => countServiceOrdersByStatus(ordersBeforeStatus),
    [ordersBeforeStatus],
  );

  const visible = useMemo(
    () =>
      sortServiceOrdersBy(
        ordersBeforeStatus.filter((order: any) => matchesServiceOrderStatus(order, statusFilter)),
        sortOption,
      ),
    [ordersBeforeStatus, statusFilter, sortOption],
  );

  /**
   * `includeDead` liga quando o recorte É o status morto: com o filtro em
   * "Canceladas", excluir todo mundo faria o hero anunciar R$ 0,00 com a lista
   * cheia embaixo.
   */
  const summary = summarizeServiceOrderList(visible, {
    includeDead: isDeadServiceOrderStatusFilter(statusFilter),
  });
  const activeModalFilters = countActiveServiceOrderFilters(periodPreset, valueRange);
  const recorteAtivo =
    search.trim().length > 0 || statusFilter !== 'all' || activeModalFilters > 0;

  const filterGroups: FilterGroup[] = [
    {
      key: PERIOD_GROUP_KEY,
      label: 'Criada em',
      options: [
        // "Qualquer data" é uma opção EXPLÍCITA, e não a ausência de seleção:
        // sem ela, quem escolheu um período não teria como voltar atrás.
        { value: '', label: 'Qualquer data' },
        ...SERVICE_ORDER_PERIOD_PRESETS.map((preset) => ({
          value: preset,
          label: PERIOD_PRESET_LABELS[preset],
        })),
      ],
    },
  ];

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ClipboardList className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Ordens de serviço</h2>
          <p className="mb-4 text-muted-foreground">
            Faça login para acessar as ordens de serviço
          </p>
          <Button3D onClick={() => navigate('/auth')}>Entrar</Button3D>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app                                     */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            {/* `/ordens-servico` não está na navegação inferior: sem este botão,
                a única saída da tela é o gesto do sistema. */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate('/dashboard')}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Documentos</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">Ordens de serviço</h1>
            </div>
            {/* A ação PRIMÁRIA fica com o rótulo CURTO: "Nova OS" na linha do
                título disputaria espaço com um h1 de 17 caracteres, e sem
                `flex-wrap` o excedente não quebra — ele sai da tela (#186). O
                nome inteiro vai no `aria-label`/`title`, onde não custa pixel
                nenhum e continua sendo o que o leitor de tela anuncia (#142). */}
            <Button3D
              size="sm"
              color="light"
              className="shrink-0"
              onClick={() => navigate('/ordens-servico/nova')}
              aria-label="Nova ordem de serviço"
              title="Nova ordem de serviço"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Nova
            </Button3D>
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">{serviceOrderListTotalLabel(statusFilter)}</p>
            {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho
                (#159-b): escrever uma regra local aqui seria mais uma cópia de
                uma medida que já divergiu entre telas uma vez. */}
            <p
              className={cn(
                'truncate font-bold leading-tight',
                heroTotalSizeClass(formatCurrency(summary.total)),
              )}
            >
              {formatCurrency(summary.total)}
            </p>
            <p className="mt-0.5 text-xs text-white/70">
              {summary.count === 0
                ? 'Nenhuma ordem neste filtro'
                : plural(summary.count, 'ordem de serviço', 'ordens de serviço')}
              {/* "no silent caps": o que ficou fora da soma é DITO. Sem isto o
                  total apareceria menor do que a lista sugere, sem explicação. */}
              {summary.excludedCount > 0 &&
                ` · ${plural(
                  summary.excludedCount,
                  'cancelada fora da conta',
                  'canceladas fora da conta',
                )}`}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* BLOCO DE CONTROLES DA LISTA — busca, recorte e ordenação ativa.
              Eles são um grupo só (todos respondem "o que eu estou vendo"),
              então ficam juntos com `space-y-2` em vez do `space-y-5` que separa
              as SEÇÕES da tela (#191/#194). */}
          <div className="space-y-2">
            {/* Busca + ordenação + filtros. Os dois ícones são `shrink-0` e o
                campo é `min-w-0 flex-1`: sem isso o input empurraria os botões
                para fora da tela em vez de encolher (#186). */}
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Cliente, título ou #0001"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Ordenação: menu, não modal — é uma escolha única entre seis
                  rótulos curtos, e um diálogo seria dois cliques a mais pelo
                  mesmo resultado. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    aria-label={`Ordenar (${SERVICE_ORDER_SORT_LABELS[sortOption]})`}
                    title={`Ordenar: ${SERVICE_ORDER_SORT_LABELS[sortOption]}`}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {SERVICE_ORDER_SORT_ORDER.map((option) => (
                    <DropdownMenuItem
                      key={option}
                      onSelect={() => setSortOption(option)}
                      className="gap-2"
                    >
                      {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                          placeholder, o rótulo escolhido fica deslocado. */}
                      <span className="flex h-4 w-4 items-center justify-center">
                        {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                      </span>
                      {SERVICE_ORDER_SORT_LABELS[option]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Filtros: aqui SIM um modal — são controles independentes que
                  fazem sentido escolhidos juntos, e o rascunho do diálogo é o
                  que impede a lista de se reorganizar a cada tecla do campo de
                  valor. */}
              <Button
                variant="outline"
                size="icon"
                className="relative h-10 w-10 shrink-0 rounded-xl"
                onClick={() => setFiltersOpen(true)}
                aria-label={
                  activeModalFilters > 0
                    ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                    : 'Filtros'
                }
                title="Filtros"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeModalFilters > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                    {activeModalFilters}
                  </span>
                )}
              </Button>
            </div>

            {/* STATUS — botões planos, não alternador (#25/#188). A contagem vai
                no `title`/`aria-label`, onde não custa pixel (#191). */}
            <div className={FILTER_CHIP_ROW}>
              {SERVICE_ORDER_STATUS_FILTER_OPTIONS.map((option) => {
                const active = statusFilter === option.value;
                // A contagem responde "quantas eu veria se clicasse aqui" — por
                // isso sai do recorte SEM o status. Um número que não bate com a
                // lista depois do clique é pior do que número nenhum.
                const count = statusCounts[option.value] ?? 0;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    aria-pressed={active}
                    // O nome acessível usa o rótulo COMPLETO: "Andam." é a
                    // abreviação que cabe na pílula, não o nome do status.
                    aria-label={`${option.srLabel} (${count})`}
                    title={`${option.srLabel}: ${plural(count, 'ordem', 'ordens')}`}
                    className={cn(FILTER_CHIP_COMPACT, active ? FILTER_CHIP_ON : FILTER_CHIP_OFF)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* A ordenação ativa é a única decisão da tela que não se anuncia
                sozinha: status tem botões e período/valor têm o selo do ícone,
                mas "por que esta ordem está no topo?" só se responde abrindo o
                menu. É rótulo, não botão — o controle é o ícone logo acima, e um
                segundo gatilho dividiria o alvo do toque. */}
            {!isLoading && visible.length > 0 && (
              <div className="flex items-center gap-1.5 px-1 text-sm text-muted-foreground">
                <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{SERVICE_ORDER_SORT_LABELS[sortOption]}</span>
              </div>
            )}

            {/* Lista — DENTRO do mesmo grupo `space-y-2` dos controles, e não um
                `-mt-N` competindo com o `space-y-5` de fora: margem negativa num
                filho direto PERDE a briga de especificidade contra o
                `space-y-*` do container (#194). */}
            {isLoading && visible.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={cn(CONTENT_CARD, 'h-24 animate-pulse')} />
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div
                className={cn(
                  CONTENT_CARD,
                  'flex flex-col items-center justify-center border-dashed py-8 text-center',
                )}
              >
                <ClipboardList className="mb-3 h-12 w-12 text-muted-foreground/50" />
                <p className="font-medium">
                  {recorteAtivo ? 'Nenhuma ordem encontrada' : 'Nenhuma ordem de serviço'}
                </p>
                <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                  {recorteAtivo
                    ? 'Nenhuma ordem corresponde à busca e aos filtros escolhidos.'
                    : 'Crie uma ordem de serviço a partir de um orçamento aprovado, ou do zero, para acompanhar a execução.'}
                </p>
                {!recorteAtivo && (
                  <Button3D className="mt-5" onClick={() => navigate('/ordens-servico/nova')}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    Nova ordem de serviço
                  </Button3D>
                )}
              </div>
            ) : (
              /* Duas colunas no desktop (padrão de `/quotes`), com
                 `grid-cols-1` explícito no celular: sem ele a coluna implícita
                 cresce até o min-content do título mais longo e vaza a página
                 inteira (#185). */
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                {visible.map((order: any) => {
                  const st = (order.status ?? 'pending') as ServiceOrderStatus;
                  const criada = timestampDate(order.created_at);
                  const agendada = scheduledDate(order.scheduled_date);
                  return (
                    <div
                      key={order.id}
                      className={cn(
                        CONTENT_CARD,
                        'flex cursor-pointer items-center gap-3 p-4 transition-shadow hover:shadow-md',
                      )}
                      onClick={() => navigate(`/ordens-servico/${order.id}`)}
                    >
                      {/* Coluna da ESQUERDA: identificação + conteúdo. Ela é
                          quem dita a altura do card — o `items-center` do card
                          faz o bloco valor+status centrar contra ELA, e não
                          contra a linha do título (#189). */}
                      <div className="min-w-0 flex-1 space-y-2">
                        {/* LINHA DE IDENTIFICAÇÃO: tudo que identifica a ordem
                            (mas não é o conteúdo dela) mora aqui, o que devolve
                            a largura inteira ao título na linha de baixo. */}
                        <div className="flex flex-wrap items-center gap-2">
                          {order._offline_pending && (
                            <Badge variant="outline" className="h-5 px-1.5 py-0 text-[10px]">
                              Offline
                            </Badge>
                          )}
                          {order.sequence_id && (
                            <Badge
                              variant="outline"
                              className="h-5 border-primary/30 bg-primary/5 px-1.5 py-0 font-mono text-xs text-primary"
                            >
                              #{String(order.sequence_id).padStart(4, '0')}
                            </Badge>
                          )}
                          {criada && (
                            <Badge
                              variant="outline"
                              className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                            >
                              {criada}
                            </Badge>
                          )}
                          {agendada && (
                            <Badge
                              variant="outline"
                              className="h-5 gap-1 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                            >
                              <Calendar className="h-3 w-3" />
                              {agendada}
                            </Badge>
                          )}
                        </div>

                        {/* `min-w-0` não é decoração (#175): sem ele um flex
                            item nasce com `min-width: auto` e um título sem
                            espaços estica a linha para fora da viewport. E sem
                            `truncate`: é o título que distingue duas linhas da
                            mesma lista, e cortá-lo come justamente o que
                            informa. */}
                        <div className="min-w-0 space-y-0.5">
                          <p className="min-w-0 break-words font-bold">
                            {order.title || order.client_name || 'Ordem de serviço'}
                          </p>
                          {order.client_name && order.title && (
                            <p className="break-words text-sm text-muted-foreground">
                              {order.client_name}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Valor + status são UM CONJUNTO, e por isso um bloco só:
                          centrados entre si no eixo horizontal e centrados na
                          ALTURA DO CARD pelo `items-center` do card (#189). */}
                      <div className="flex shrink-0 flex-col items-center gap-1">
                        <p className="font-semibold">
                          {formatCurrency(Number(order.total ?? 0))}
                        </p>
                        <Badge className={cn('border-0', STATUS_STYLES[st])}>
                          {statusLabel(st)}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filtros: quando a ordem foi criada e a faixa de valor.
          O STATUS aparece só na faixa de botões, e não aqui, porque ele já se
          anuncia sozinho na tela — repeti-lo no modal duplicaria o controle sem
          revelar nada que o usuário não esteja vendo. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte as ordens por quando foram criadas e por valor."
        groups={filterGroups}
        rangeLabel="Valor da ordem"
        value={{ groups: { [PERIOD_GROUP_KEY]: periodPreset ?? '' }, range: valueRange }}
        onApply={(next) => {
          const wanted = next.groups[PERIOD_GROUP_KEY];
          setPeriodPreset(
            wanted && SERVICE_ORDER_PERIOD_PRESETS.includes(wanted as ReportPeriodPreset)
              ? (wanted as ReportPeriodPreset)
              : null,
          );
          setValueRange(next.range);
        }}
      />
    </MainLayout>
  );
}
